// layouts/PostSimple.tsx (or your actual filename)

// --- 1. Imports ---
import { ReactNode } from 'react';
import { formatDate } from 'pliny/utils/formatDate';
// (Removed CoreContent, Blog)

// --- 2. Import DjangoPost Type ---
import type { DjangoPost } from '@/lib/data'; // Adjust path if needed
import Comments from '@/components/Comments';
import Link from '@/components/Link';
import PageTitle from '@/components/PageTitle';
import SectionContainer from '@/components/SectionContainer';
import siteMetadata from '@/data/siteMetadata';
import ScrollTopAndComment from '@/components/ScrollTopAndComment';

// --- 4. Update LayoutProps Interface ---
interface LayoutProps {
  content: DjangoPost; // <-- Use DjangoPost
  children: ReactNode;
  next?: DjangoPost | null; // <-- Also DjangoPost
  prev?: DjangoPost | null; // <-- Also DjangoPost
}

// --- 5. Update Component Function Signature ---
export default function PostLayout({ content, next, prev, children }: LayoutProps) {
  // --- 6. Fix Destructuring ---
  // const { path, slug, date, title } = content; // <-- Old
  const { slug, created_at: date, title } = content; // <-- New (no 'path')

  // --- 7. Define basePath (needed for prev/next links) ---
  const basePath = 'blog'; // Assuming posts are under /blog

  return (
    <SectionContainer>
      <ScrollTopAndComment />
      <article>
        <div>
          <header>
            <div className="space-y-1 border-b border-gray-200 pb-10 text-center dark:border-gray-700">
              <dl>
                <div>
                  <dt className="sr-only">Published on</dt>
                  <dd className="text-base leading-6 font-medium text-gray-500 dark:text-gray-400">
                    {/* 'date' is now 'created_at', formatDate should work */}
                    <time dateTime={date}>{formatDate(date, siteMetadata.locale)}</time>
                  </dd>
                </div>
              </dl>
              <div>
                {/* 'title' works */}
                <PageTitle>{title}</PageTitle>
              </div>
            </div>
          </header>
          <div className="grid-rows-[auto_1fr] divide-y divide-gray-200 pb-8 xl:divide-y-0 dark:divide-gray-700">
            <div className="divide-y divide-gray-200 xl:col-span-3 xl:row-span-2 xl:pb-0 dark:divide-gray-700">
              {/* 'children' (ReactMarkdown) renders here */}
              <div className="prose dark:prose-invert max-w-none pt-10 pb-8">{children}</div>
            </div>
            {siteMetadata.comments && (
              <div className="pt-6 pb-6 text-gray-700 dark:text-gray-300" id="comment">
                {/* 'slug' works */}
                <Comments slug={slug} />
              </div>
            )}
            <footer>
              <div className="flex flex-col text-sm font-medium sm:flex-row sm:justify-between sm:text-base">
                {/* --- 8. Fix Prev Link --- */}
                {prev &&
                  prev.slug && ( // Check for prev.slug
                    <div className="pt-4 xl:pt-8">
                      <Link
                        href={`/${basePath}/${prev.slug}`} // <-- Use slug
                        className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
                        aria-label={`Previous post: ${prev.title}`}
                      >
                        &larr; {prev.title}
                      </Link>
                    </div>
                  )}
                {/* --- 9. Fix Next Link --- */}
                {next &&
                  next.slug && ( // Check for next.slug
                    <div className="pt-4 xl:pt-8">
                      <Link
                        href={`/${basePath}/${next.slug}`} // <-- Use slug
                        className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
                        aria-label={`Next post: ${next.title}`}
                      >
                        {next.title} &rarr;
                      </Link>
                    </div>
                  )}
              </div>
            </footer>
          </div>
        </div>
      </article>
    </SectionContainer>
  );
}
